import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MouseSimple, Coins, ChartLine, UsersThree, Trophy, PlusCircle, Megaphone, Globe, Star, ArrowRight, Wallet, Question, Storefront, Palette, Rocket, Flag, TextT, ArrowClockwise } from "@phosphor-icons/react";
import UserBadge from "@/components/UserBadge";
import AchievementBadges from "@/components/AchievementBadges";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [sites, setSites] = useState([]);

  useEffect(() => {
    api.get("/credits/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/announcements").then((r) => setAnnouncements(r.data)).catch(() => {});
    api.get("/websites").then((r) => setSites(r.data)).catch(() => {});
  }, []);

  const statCards = [
    { label: "Cash Wallet", value: `$${Number(stats?.cash_wallet ?? 0).toFixed(2)}`, icon: Wallet, color: "text-emerald-600", testid: "stat-cash-wallet" },
    { label: "Credits", value: stats?.credits?.toFixed(1) ?? "—", icon: Coins, color: "text-yellow-500", testid: "stat-credits" },
    { label: "XP", value: stats?.xp ?? "—", icon: Trophy, color: "text-blue-500", testid: "stat-xp" },
    { label: "Surfs (Today)", value: stats?.daily_surfs ?? "—", icon: MouseSimple, color: "text-green-600", testid: "stat-daily-surfs" },
    { label: "Total Surfs", value: stats?.total_surfs ?? "—", icon: ChartLine, color: "text-purple-500", testid: "stat-total-surfs" },
  ];

  return (
    <DashboardLayout>
      {/* Big surfing panels */}
      <div className="grid md:grid-cols-2 gap-4">
        <Link
          to="/surf-deal?next=/surf"
          data-testid="start-surfing-btn-primary"
          className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white p-6 md:p-8 shadow-lg hover:-translate-y-1 transition-transform duration-200 flex items-center gap-5"
        >
          <div className="h-16 w-16 rounded-full bg-white/20 flex items-center justify-center shrink-0">
            <MouseSimple size={36} weight="fill" />
          </div>
          <div className="flex-1">
            <div className="text-xs font-bold uppercase tracking-widest text-white/80">1:1 Standard Surf</div>
            <div className="text-3xl md:text-4xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Start Surfing</div>
            <div className="text-sm text-white/90 mt-1">Earn credits and drive real visitors to your site.</div>
          </div>
          <ArrowRight size={28} weight="bold" className="opacity-70 group-hover:translate-x-1 transition-transform duration-200" />
        </Link>
        <Link
          to="/websites"
          data-testid="dashboard-manage-sites-btn"
          className="group relative overflow-hidden rounded-2xl bg-blue-700 hover:bg-blue-600 text-white p-6 md:p-8 shadow-lg hover:-translate-y-1 transition-transform duration-200 flex items-center gap-5"
        >
          <div className="h-16 w-16 rounded-full bg-white/20 flex items-center justify-center shrink-0">
            <Globe size={36} weight="fill" />
          </div>
          <div className="flex-1">
            <div className="text-xs font-bold uppercase tracking-widest text-white/80">Manage</div>
            <div className="text-3xl md:text-4xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>My Websites</div>
            <div className="text-sm text-white/90 mt-1">{sites.length} site{sites.length !== 1 ? "s" : ""} — submit, edit, activate.</div>
          </div>
          <ArrowRight size={28} weight="bold" className="opacity-70 group-hover:translate-x-1 transition-transform duration-200" />
        </Link>
      </div>

      <div className="mt-6 grid lg:grid-cols-4 gap-4">
        {/* Left sidebar - actions */}
        <aside className="lg:col-span-1 space-y-3">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-3">Quick actions</div>
            <div className="space-y-1">
              {[
                { to: "/websites/new", label: "Submit Website", icon: PlusCircle, testid: "quick-submit-site" },
                { to: "/splash", label: "Splash Pages", icon: Palette, testid: "quick-splash-pages" },
                { to: "/start-pages", label: "Start Pages", icon: Rocket, testid: "quick-start-pages" },
                { to: "/buy-credits", label: "Buy Credits", icon: Coins, testid: "quick-buy-credits" },
                { to: "/buy-upgrades", label: "Buy Upgrades", icon: Star, testid: "quick-buy-upgrades" },
                { to: "/buy-banners", label: "Buy Banners", icon: Flag, testid: "quick-buy-banners" },
                { to: "/my-banners", label: "My Banners", icon: Flag, testid: "quick-my-banners" },
                { to: "/buy-text-ads", label: "Buy Text Ads", icon: TextT, testid: "quick-buy-text-ads" },
                { to: "/my-text-ads", label: "My Text Ads", icon: TextT, testid: "quick-my-text-ads" },
                { to: "/surf-drawn-winners", label: "Surf Drawn Winners", icon: Trophy, testid: "quick-surf-drawn-winners" },
                { to: "/my-rotors", label: "TE Website Rotors", icon: ArrowClockwise, testid: "quick-rotors" },
                { to: "/referrals", label: "Referrals", icon: UsersThree, testid: "quick-referrals" },
                { to: "/statistics", label: "Stats", icon: ChartLine, testid: "quick-statistics" },
              ].map((l) => (
                <Link key={l.to} to={l.to} data-testid={l.testid} className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 transition-colors duration-200">
                  <l.icon size={20} weight="bold" className="text-blue-600 dark:text-blue-400" />
                  <span className="font-semibold text-sm">{l.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </aside>

        {/* Main content */}
        <section className="lg:col-span-2 space-y-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2">My Dashboard</div>
            <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white inline-flex items-center gap-2 flex-wrap" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Welcome back, {user?.username}<UserBadge user={user} size={22} /><AchievementBadges userId={user?.id} size={20} /> 👋</h1>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {statCards.map((c) => (
              <div key={c.label} data-testid={c.testid} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 hover:shadow-lg hover:-translate-y-1 transition-transform duration-200">
                <c.icon size={26} weight="fill" className={c.color} />
                <div className="text-3xl font-black tracking-tight text-slate-900 dark:text-white mt-2" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{c.value}</div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mt-1">{c.label}</div>
              </div>
            ))}
          </div>

          {/* Announcements */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Megaphone size={20} weight="fill" className="text-yellow-500" />
              <div className="text-sm font-black uppercase tracking-widest text-slate-700 dark:text-slate-200">Announcements</div>
            </div>
            {announcements.length === 0 && <div className="text-sm text-slate-500">No announcements right now.</div>}
            <div className="space-y-3">
              {announcements.slice(0, 3).map((a) => (
                <div key={a.id} className="border-l-4 border-yellow-400 pl-3">
                  <div className="font-bold text-slate-900 dark:text-white">{a.title}</div>
                  <div className="text-sm text-slate-600 dark:text-slate-300">{a.body}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Right sidebar */}
        <aside className="lg:col-span-1 space-y-4">
          <div className="rounded-2xl bg-blue-700 text-white p-5">
            <div className="text-xs font-bold uppercase tracking-widest text-white/80">Unassigned credits</div>
            <div className="text-4xl font-black tracking-tight mt-1" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{stats?.credits?.toFixed(1) ?? "—"}</div>
            <div className="text-sm text-white/90 mt-1">Assign credits to your websites to receive visitors.</div>
            <Link to="/websites" data-testid="assign-credits-btn" className="mt-3 inline-flex items-center gap-2 rounded-full bg-yellow-400 text-yellow-950 font-bold px-4 py-2 hover:bg-yellow-300 transition-colors duration-200">
              Assign now <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Membership</div>
            <div className="text-2xl font-black tracking-tight text-slate-900 dark:text-white mt-1" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{user?.membership || "Free"}</div>
            <button data-testid="shop-upgrades-cta-btn" onClick={() => navigate("/buy-upgrades")} className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-blue-700 dark:text-blue-400 hover:underline">
              Buy Upgrades <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        </aside>
      </div>
    </DashboardLayout>
  );
}
